import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  TextField,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import * as d3 from 'd3';
import { organizationApi, employeesApi, skillsApi } from '../../services/api';
import { useAlert } from '../../contexts/AlertContext';
import { useAbility } from '../../contexts/AbilityContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { EmployeeAvatar, getInitials } from '../common/EmployeeAvatar';
import { HeaderButton } from '../common/styles';
import EmployeeSearch from '../common/EmployeeSearch';
import SkillsMatrixSelectionList from './common/SkillsMatrixSelectionList';
import {
  getTemplateTypeConstant,
  MATRIX_TEMPLATE_TYPES
} from '../../constants/matrixTemplateTypes';
import { REVIEW_TYPES } from '../../constants/reviewTypes';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { ACTIONS, SUBJECTS } from '../../constants/permissions';

const TeamStructureView = ({ 
  businessUnit, 
  onEmployeeSelection, 
  employeeAssignments,
  reviewType,
  readOnly,
  onReviewerAssignment,
  selectedProjects,
  onProjectSelection,
  selectedSessionTemplate,
  isIndividualMode = false,
}) => {
  const { showAlert } = useAlert();
  const ability = useAbility();
  const [isLoading, setIsLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState(null);
  const svgRef = useRef();
  const [isMatrixDialogOpen, setIsMatrixDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [uniqueMatrices, setUniqueMatrices] = useState([]);
  const [allMatrices, setAllMatrices] = useState([]);
  const [templatesData, setTemplatesData] = useState([]);
  const [isOverrideMode, setIsOverrideMode] = useState(false);

  // Check if user can create extra evaluees
  const canCreateExtraEvaluees = () => {
    const extraEvaluationObject = {
      __type: SUBJECTS.EXTRA_EVALUATION
    };
    
    return ability.can(ACTIONS.CREATE, extraEvaluationObject);
  };

  // Check if user can select projects
  const canSelectProjects = () => {
    const projectObject = {
      __type: SUBJECTS.FEEDBACK_SESSION_PROJECT
    };
    
    return ability.can(ACTIONS.CREATE, projectObject);
  };

  // Check if user can view business unit owners
  const canViewBusinessUnitOwner = () => {
    const extraEvaluationObject = {
      __type: SUBJECTS.EXTRA_EVALUATION
    };
    
    return ability.can(ACTIONS.READ, extraEvaluationObject);
  };

  // Extra evaluees state
  const [extraEvaluees, setExtraEvaluees] = useState([]);
  const [isExtraEvalueesDialogOpen, setIsExtraEvalueesDialogOpen] = useState(false);
  const [selectedExtraEvaluee, setSelectedExtraEvaluee] = useState(null);

  // Store collapsed nodes state
  const [collapsedNodes, setCollapsedNodes] = useState(new Set());
  
  // For storing original matrices when filtering
  const [originalMatrices, setOriginalMatrices] = useState([]);

  // Update extraEvaluees when employeeAssignments changes
  useEffect(() => {
    // Update extraEvaluees to reflect the deleted status from employeeAssignments
    setExtraEvaluees(prev => {
      // Create a new array to store updated evaluees
      const updated = [];
      
      // First include all non-deleted extra evaluees from the previous state
      prev.forEach(evaluee => {
        const assignment = employeeAssignments.get(evaluee.id);
        if (assignment && assignment.deleted !== true) {
          updated.push(evaluee);
        }
      });
      
      return updated;
    });
  }, [employeeAssignments]);

  // Populate extraEvaluees when the component mounts or when hierarchyData or employeeAssignments changes
  useEffect(() => {
    if (!hierarchyData || !employeeAssignments.size) return;

    // Get all employee IDs from the hierarchy
    const hierarchyEmployeeIds = getAllEmployeeIdsFromHierarchy(hierarchyData);
    
    // Find employees in employeeAssignments that are not in the hierarchy and not deleted
    const newExtraEvaluees = [];
    
    const fetchExtraEvaluees = async () => {
      for (const [employeeId, assignment] of employeeAssignments.entries()) {
        // Skip deleted employees
        if (assignment.deleted === true) continue;
        
        // Skip if already in hierarchy or already in extraEvaluees
        if (hierarchyEmployeeIds.has(employeeId) || 
            extraEvaluees.some(evaluee => evaluee.id === employeeId)) {
          continue;
        }
        
        try {
          // Fetch the employee data using the employeeId
          const employeeData = assignment.employee;
          
          if (employeeData) {
            newExtraEvaluees.push({
              id: employeeId,
              fullName: employeeData.fullName || 'Unknown',
              position: employeeData.position,
              grade: employeeData.grade,
              azureId: employeeData.azureId,
              engagementType: employeeData.engagementType,
              roles: employeeData.roles || [],
              matrix: assignment.matrix, // Include the matrix information
              isExtraEvaluee: true
            });
          }
        } catch (error) {
          console.error(`Error fetching employee data for ID ${employeeId}:`, error);
        }
      }
      
      if (newExtraEvaluees.length > 0) {
        setExtraEvaluees(prev => [...prev, ...newExtraEvaluees]);
      }
    };
    
    fetchExtraEvaluees();
  }, [hierarchyData, employeeAssignments, extraEvaluees]);

  // Fetch templates data using the session ID
  useEffect(() => {
    if (selectedSessionTemplate?.id) {
      const fetchTemplatesData = async () => {
        try {
          setIsLoading(true);
          const templates = await skillsApi.getSkillsMatrixTemplatesBySessionId(selectedSessionTemplate.id);
          setTemplatesData(templates);

          // Extract unique skills matrices
          const matricesMap = new Map();
          templates.forEach(template => {
            if (template.skillsMatrix && !matricesMap.has(template.skillsMatrix.id)) {
              matricesMap.set(template.skillsMatrix.id, template.skillsMatrix);
            }
          });
          const matrices = Array.from(matricesMap.values());
          setAllMatrices(matrices);
          setUniqueMatrices(matrices);
        } catch (error) {
          showAlert('Failed to fetch templates data', 'error', error);
          console.error('Error fetching templates:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchTemplatesData();
    }
  }, [selectedSessionTemplate]);

  useEffect(() => {
    if (businessUnit?.id) {
      fetchHierarchyData();
    } else if (isIndividualMode) {
      // In individual mode without a business unit, set empty hierarchy
      setHierarchyData({
        id: 'empty',
        name: 'No Business Unit',
        projects: [],
        childBus: []
      });
    }
  }, [businessUnit, isIndividualMode]);


  const getEmployeeTemplate = (employeeId) => {
    const assignment = employeeAssignments.get(employeeId);
    if (!assignment?.matrix) return null;

    return templatesData.find(template => 
      template.skillsMatrix && 
      template.skillsMatrix.id === assignment.matrix.id
    );
  };

  const fetchHierarchyData = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getBusinessUnitDetailedHierarchy(businessUnit.id);
      setHierarchyData(data);
    } catch (error) {
      showAlert(error.message || 'Failed to fetch team structure', 'error', error);
      console.error('Error fetching team structure:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Common function to filter matrices based on employee attributes
  const filterMatricesByEmployeeAttributes = (employee, matrices) => {
    // First, filter matrices by position
    let positionFilteredMatrices = matrices;
    
    if (employee.position) {
      // Try to find matrices that match the employee's position
      const positionMatches = matrices.filter(matrix => 
        matrix.position?.id === employee.position.id
      );
      
      // If we found any position matches, use only those
      if (positionMatches.length > 0) {
        positionFilteredMatrices = positionMatches;
      }
    }

    // Then, among the position-filtered matrices, check for role matching
    const filteredMatrices = positionFilteredMatrices.filter(matrix => {
      // If matrix has matrixRoles property, check if all exist in employee roles
      if (matrix.matrixRoles && matrix.matrixRoles.length > 0 && employee.roles && employee.roles.length > 0) {
        return matrix.matrixRoles.some(matrixRole => 
          employee.roles.some(employeeRole => 
            employeeRole.id === matrixRole.role?.id
          )
        );
      }
      
      // If matrix doesn't have specific roles defined or employee has no roles, include it
      return true;

    });
    // If no matrices match the role criteria but we have position matches, fall back to those
    if (filteredMatrices.length === 0 && positionFilteredMatrices.length > 0) {
      return positionFilteredMatrices;
    }

    return filteredMatrices;
  };

  // Helper function to check if an employee belongs to a selectable project
  const isEmployeeInSelectableProject = (employee) => {
    // In individual mode, all employees are selectable
    if (isIndividualMode) {
      return true;
    }
    
    // If the employee is a business unit owner or extra evaluee, they're not in a project
    if (employee.isBusinessUnitOwner || employee.isExtraEvaluee) {
      return true;
    }
    
    // Find the project ID for this employee
    const projectId = findProjectIdForEmployee(hierarchyData, employee.id);
    
    // If no project found, allow interaction (default behavior)
    if (!projectId) return true;
    
    // Check if the project is selected and user can interact with projects
    const isProjectSelected = (selectedProjects || new Set()).has(projectId);
    const canInteractWithProject = canSelectProjects() && !readOnly;
    
    // Allow interaction only if:
    // 1. Project is selected, OR
    // 2. User cannot interact with projects at all (fall back to normal behavior)
    return isProjectSelected || canInteractWithProject;
  };

  const handleOpenMatrixOverride = (employee) => {
    setOriginalMatrices(uniqueMatrices);
    setUniqueMatrices(allMatrices);
    setSelectedEmployee(employee);
    setIsOverrideMode(true);
    setIsMatrixDialogOpen(true);
  };

  const handleEmployeeClick = (employee) => {
    // Add check for project selectability
    if (!isEmployeeInSelectableProject(employee)) {
      return;
    }

    // Check if employee has an assignment
    if (employeeAssignments.has(employee.id)) {
      // Get the employee's assignment
      const assignment = employeeAssignments.get(employee.id);
      
      // If the employee is deleted, restore them
      if (assignment.deleted !== true) {
        // If this is an extra evaluee, also remove it from the extraEvaluees list
        if (employee.isExtraEvaluee) {
          setExtraEvaluees(prev => prev.filter(evaluee => evaluee.id !== employee.id));
        }

        // If not deleted, mark as deleted
        onEmployeeSelection(employee.id, employee, null);
        return;
      }

    }

    setIsOverrideMode(false);

    // Filter matrices using the common function
    const filteredMatrices = filterMatricesByEmployeeAttributes(employee, allMatrices);
    
    // If there's exactly one match, select it automatically without showing the dialog
    if (filteredMatrices.length === 1) {
      handleMatrixSelect(filteredMatrices[0], employee);
      
      // Select the project this employee belongs to (if applicable)
      selectEmployeeProject(employee);
      return;
    }
    
    // Set the filtered matrices for the dialog
    const matricesToShow = filteredMatrices.length > 0 ? filteredMatrices : allMatrices;

    // Store the original matrices in state
    setOriginalMatrices(uniqueMatrices);

    // Update uniqueMatrices with the filtered list
    setUniqueMatrices(matricesToShow);
    
    // Then show the dialog with the selected employee
    setSelectedEmployee(employee);
    setIsMatrixDialogOpen(true);
  };

  const handleMatrixSelect = (matrix, employeeOverride = null) => {
    const employee = employeeOverride || selectedEmployee;
    
    // First, call the original onEmployeeSelection to set up the initial assignment
    onEmployeeSelection(employee.id, employee, matrix);
    
    // Select the project this employee belongs to (if applicable)
    selectEmployeeProject(employee);
    
    // Create an array to hold reviewer assignments
    const reviewerAssignments = [];

    if (reviewType === REVIEW_TYPES.PARALLEL) {
      // Find the self-assessment template for this matrix
      const selfAssessmentTemplate = templatesData.find(template => 
        template.skillsMatrix?.id === matrix.id && 
        getTemplateTypeConstant(template.type) === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT
      );
    
      // If we found a self-assessment template, add it to the employee's reviewers
      if (selfAssessmentTemplate) {
        // Create the self-assessment reviewer assignment
        const selfAssessmentReviewerAssignment = {
          reviewer: employee, // Use the employee themselves as the reviewer
          template: selfAssessmentTemplate,
          reviewType: REVIEW_TYPES.PARALLEL
        };
        
        reviewerAssignments.push(selfAssessmentReviewerAssignment);
      }
    }

    
    // If review type is Sequential, check if we need to add a technical assessment
    if (reviewType === REVIEW_TYPES.SEQUENTIAL) {
      // Find the technical assessment template for this matrix
      const technicalAssessmentTemplate = templatesData.find(template => 
        template.skillsMatrix?.id === matrix.id && 
        getTemplateTypeConstant(template.type) === MATRIX_TEMPLATE_TYPES.TECHNICAL_ASSESSMENT
      );
      
      // If we found a technical assessment template, add it to the employee's reviewers
      if (technicalAssessmentTemplate) {
        // Create the technical assessment reviewer assignment
        const reviewerAssignment = {
          reviewer: employee, // Use the employee themselves as the reviewer
          template: technicalAssessmentTemplate,
          reviewType: REVIEW_TYPES.SEQUENTIAL,
          order: 1 // Set as first in the sequence
        };
        
        reviewerAssignments.push(reviewerAssignment);
      }
    }
    
    if (onReviewerAssignment) {
      onReviewerAssignment(employee.id, reviewerAssignments);
    }

    if (isOverrideMode) {
      if (reviewerAssignments.length > 0) {
        showAlert('Skills matrix updated. Default reviewer has been reset.', 'info');
      } else {
        showAlert('Skills matrix updated. Existing reviewers cleared — no default reviewer for this matrix.', 'info');
      }
    }
    
    // Restore original matrices if they were filtered
    if (originalMatrices.length > 0) {
      setUniqueMatrices(originalMatrices);
      setOriginalMatrices([]);
    }
    
    setIsMatrixDialogOpen(false);
    setSelectedEmployee(null);
    setIsOverrideMode(false);
  };

  const handleCloseDialog = () => {
    setIsMatrixDialogOpen(false);
    setIsOverrideMode(false);

    // If we have temporarily replaced uniqueMatrices with filtered ones,
    // restore from originalMatrices
    if (originalMatrices.length > 0) {
      setUniqueMatrices(originalMatrices);
      setOriginalMatrices([]);
    }

    
    // If we were selecting a matrix for an extra evaluee, reset the state
    if (selectedExtraEvaluee) {
      setSelectedExtraEvaluee(null);
    } else {
      setSelectedEmployee(null);
    }
    
  };

  // Extra evaluees functions
  const handleRemoveExtraEvaluee = (employeeId) => {
    setExtraEvaluees(prev => prev.filter(evaluee => evaluee.id !== employeeId));
    onEmployeeSelection(employeeId, null, null);
  };

  const handleOpenExtraEvalueesDialog = () => {
    // Reset matrices to original if they were filtered
    if (originalMatrices.length > 0) {
      setUniqueMatrices(originalMatrices);
      setOriginalMatrices([]);
    }
    
    setIsExtraEvalueesDialogOpen(true);
    setSelectedExtraEvaluee(null);
  };

  const handleCloseExtraEvalueesDialog = () => {
    // Reset matrices to original if they were filtered
    if (originalMatrices.length > 0) {
      setUniqueMatrices(originalMatrices);
      setOriginalMatrices([]);
    }
    
    setIsExtraEvalueesDialogOpen(false);
    setSelectedExtraEvaluee(null);
  };

  // Calculate disabled employee IDs based on the same logic as fetchAllEmployees
  const getDisabledEmployeeIds = () => {
    if (!hierarchyData) return [];

    // Get all employee IDs from the hierarchy
    const hierarchyEmployeeIds = getAllEmployeeIdsFromHierarchy(hierarchyData);
    const disabledIds = [];

    // Add employees that are in hierarchy but NOT deleted (these should be disabled)
    hierarchyEmployeeIds.forEach(employeeId => {
      const isDeleted = employeeAssignments.has(employeeId) && 
                        employeeAssignments.get(employeeId).deleted === true;
      
      // If employee is in hierarchy but NOT deleted, disable them
      if (!isDeleted) {
        disabledIds.push(employeeId);
      }
    });

    // Add employees that are already in extraEvaluees and not deleted
    extraEvaluees.forEach(evaluee => {
      const assignment = employeeAssignments.get(evaluee.id);
      const isDeleted = assignment && assignment.deleted === true;
      
      // If evaluee is not deleted, disable them
      if (!isDeleted) {
        disabledIds.push(evaluee.id);
      }
    });

    return disabledIds;
  };


  const handleExtraEvalueeSelect = (employee) => {
    if (!employee) {
      setSelectedExtraEvaluee(null);
      return;
    }
    
    setSelectedExtraEvaluee(employee);
    
    // Filter matrices using the common function
    const filteredMatrices = filterMatricesByEmployeeAttributes(employee, allMatrices);
    
    // If there's exactly one match, select it automatically without showing the dialog
    if (filteredMatrices.length === 1) {
      // Pass the employee directly to avoid race conditions with state
      handleExtraEvalueeMatrixSelect(filteredMatrices[0], employee);
      return;
    }
    
    // Set the filtered matrices for the dialog
    const matricesToShow = filteredMatrices.length > 0 ? filteredMatrices : allMatrices;

    // Store the original matrices in state
    setOriginalMatrices(uniqueMatrices);

    // Update uniqueMatrices with the filtered list
    setUniqueMatrices(matricesToShow);
  };

  const handleExtraEvalueeMatrixSelect = (matrix, employee = null) => {
    // Use the passed employee parameter or fall back to the state value
    const evaluee = employee || selectedExtraEvaluee;
    
    // Check if the employee was previously deleted
    const wasDeleted = employeeAssignments.has(evaluee.id) && 
                     employeeAssignments.get(evaluee.id).deleted === true;
    
    // If it was marked as deleted, we're essentially restoring it
    if (wasDeleted) {
      // Get the existing assignment to preserve any existing reviewers
      const existingAssignment = employeeAssignments.get(evaluee.id);
      
      // If we're re-adding an employee that was previously deleted, we need to
      // preserve any existing reviewer assignments they had
      onEmployeeSelection(evaluee.id, evaluee, matrix);
      
      // Re-add existing reviewers if they had any and onReviewerAssignment is available
      if (existingAssignment.reviewers && existingAssignment.reviewers.length > 0 && onReviewerAssignment) {
        onReviewerAssignment(evaluee.id, existingAssignment.reviewers);
      } else {
        // Create self-assessment if needed
        setupDefaultReviewers(evaluee, matrix);
      }
    } else {
      // Add the employee to extraEvaluees with the selected matrix
      const newExtraEvaluee = {
        ...evaluee,
        matrix: matrix,
        isExtraEvaluee: true
      };
      
      setExtraEvaluees(prev => [...prev, newExtraEvaluee]);
      
      // Call onEmployeeSelection to set up the assignment
      onEmployeeSelection(evaluee.id, evaluee, matrix);
      
      // Set up default reviewer assignments (self-assessment, etc.)
      setupDefaultReviewers(evaluee, matrix);
    }
    
    // Restore original matrices if they were filtered
    if (originalMatrices.length > 0) {
      setUniqueMatrices(originalMatrices);
      setOriginalMatrices([]);
    }
    
    setIsMatrixDialogOpen(false);
    setIsExtraEvalueesDialogOpen(false);
    setSelectedExtraEvaluee(null);
  };
  
  // Extract the reviewer setup logic to a helper function
  const setupDefaultReviewers = (employee, matrix) => {
    // Create an array to hold reviewer assignments
    const reviewerAssignments = [];
    
    if (reviewType === REVIEW_TYPES.PARALLEL) {
      // Find the self-assessment template for this matrix
      const selfAssessmentTemplate = templatesData.find(template => 
        template.skillsMatrix?.id === matrix.id && 
        getTemplateTypeConstant(template.type) === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT
      );
    
      // If we found a self-assessment template, add it to the employee's reviewers
      if (selfAssessmentTemplate) {
        // Create the self-assessment reviewer assignment
        const selfAssessmentReviewerAssignment = {
          reviewer: employee, // Use the employee themselves as the reviewer
          template: selfAssessmentTemplate,
          reviewType: REVIEW_TYPES.PARALLEL,
        };
        
        reviewerAssignments.push(selfAssessmentReviewerAssignment);
      }
    }
    
    // If review type is Sequential, check if we need to add a technical assessment
    if (reviewType === REVIEW_TYPES.SEQUENTIAL) {
      // Find the technical assessment template for this matrix
      const technicalAssessmentTemplate = templatesData.find(template => 
        template.skillsMatrix?.id === matrix.id && 
        getTemplateTypeConstant(template.type) === MATRIX_TEMPLATE_TYPES.TECHNICAL_ASSESSMENT
      );
      
      // If we found a technical assessment template, add it to the employee's reviewers
      if (technicalAssessmentTemplate) {
        // Create the technical assessment reviewer assignment
        const reviewerAssignment = {
          reviewer: employee, // Use the employee themselves as the reviewer
          template: technicalAssessmentTemplate,
          reviewType: REVIEW_TYPES.SEQUENTIAL,
          order: 1 // Set as first in the sequence
        };
        
        reviewerAssignments.push(reviewerAssignment);
      }
    }
    
    // If we have any reviewer assignments, call onReviewerAssignment
    if (reviewerAssignments.length > 0 && onReviewerAssignment) {
      onReviewerAssignment(employee.id, reviewerAssignments);
    }
  };

  // Helper function to get unique roles for a matrix
  const getMatrixRoles = (matrix) => {
    const rolesMap = new Map();
    templatesData
      .filter(template => template.skillsMatrix && template.skillsMatrix.id === matrix.id)
      .forEach(template => {
        if (template.type) {
          const key = `${template.type}-${template.version || 'no-version'}`;
          rolesMap.set(key, {
            type: template.type,
            version: template.version
          });
        }
      });
    return Array.from(rolesMap.values());
  };

  useEffect(() => {
    if (!hierarchyData || !svgRef.current) return;

    const margin = { top: 0, right: 40, bottom: 10, left: 40 };
    const barHeight = 66; //56;
    const treeIndent = 20;

    // Transform data for D3
    const transformTeamData = (team) => ({
      id: team.id,
      name: team.depth === 0 ? `Team: ${team.name}` : team.name,
      manager: team.manager,
      type: 'team',
      hasChildren: team.childTeams?.length > 0 || team.employees?.length > 0,
      depth: (team.depth || 0),
      children: [
        ...(team.employees?.map(employee => {
          // Add all employees to the tree, regardless of deletion status
          // We'll handle the visual styling of deleted employees in nodeGroup.each
          const assignment = employeeAssignments.get(employee.id);
          const isDeleted = assignment && assignment.deleted === true;
          
          return {
            id: employee.id,
            fullName: employee.fullName,
            type: 'employee',
            hasChildren: false,
            position: employee.position,
            grade: employee.grade,
            azureId: employee.azureId,
            engagementType: employee.engagementType,
            roles: employee.roles,
            isDeleted: isDeleted // Track deletion status
          };
        }) || []),
        ...(team.childTeams ? team.childTeams.map(child => transformTeamData({...child, depth: (team.depth || 0) + 1})) : [])
      ]
    });

    const transformProjectData = (project) => ({
      id: project.id,
      name: `Project: ${project.name}`,
      type: 'project',
      hasChildren: project.teams?.length > 0,
      children: project.teams ? project.teams.map(team => transformTeamData({...team, depth: 0})) : []
    });

    // Extract all business unit owners from the current business unit and its child business units
    const extractBusinessUnitData = (bu) => ({
      id: bu.id,
      name: bu.depth === 0 ? `Business Unit: ${bu.name}` : bu.name,
      manager: bu.owner,
      type: 'businessUnit',
      hasChildren: (bu.projects?.length > 0 || bu.childBus?.length > 0 || (canViewBusinessUnitOwner() && bu.owner) || extraEvaluees.length > 0),
      children: [
        // Add the owner as an employee node if it exists and user has permission
        // Business owners are always shown, regardless of deletion status
        ...(canViewBusinessUnitOwner() && bu.owner ? [{
          id: bu.owner.id,
          fullName: bu.owner.fullName || bu.owner.name,
          type: 'employee',
          hasChildren: false,
          position: bu.owner.position,
          grade: bu.owner.grade,
          azureId: bu.owner.azureId,
          engagementType: bu.owner.engagementType,
          roles: [{ label: 'Business Unit Owner' }],
          isBusinessUnitOwner: true
        }] : []),
        // Add extra evaluees that are not deleted
        ...extraEvaluees
          .filter(evaluee => {
            const assignment = employeeAssignments.get(evaluee.id);
            return assignment && assignment.deleted !== true;
          })
          .map(evaluee => ({
            id: evaluee.id,
            fullName: evaluee.fullName,
            type: 'employee',
            hasChildren: false,
            position: evaluee.position,
            grade: evaluee.grade,
            azureId: evaluee.azureId,
            engagementType: evaluee.engagementType,
            roles: evaluee.roles || [],
            isExtraEvaluee: true
          })),
        ...(isIndividualMode ? [] : (bu.projects?.map(transformProjectData) || [])),
        ...(bu.childBus?.map(child => extractBusinessUnitData({...child, depth: (bu.depth || 0) + 1})) || [])
      ]
    });

    // Create root hierarchy
    let rootData;
    
    if (isIndividualMode) {
      // In individual mode, always show the Individual Assignments structure
      const extraEvalueesFiltered = extraEvaluees.filter(evaluee => {
        const assignment = employeeAssignments.get(evaluee.id);
        return assignment && assignment.deleted !== true;
      });
      
      rootData = {
        id: 'extra-evaluees',
        name: 'Individual Assignments',
        type: 'businessUnit',
        hasChildren: extraEvalueesFiltered.length > 0,
        children: extraEvalueesFiltered.map(evaluee => ({
          id: evaluee.id,
          fullName: evaluee.fullName,
          type: 'employee',
          hasChildren: false,
          position: evaluee.position,
          grade: evaluee.grade,
          azureId: evaluee.azureId,
          engagementType: evaluee.engagementType,
          roles: evaluee.roles || [],
          isExtraEvaluee: true
        }))
      };
    } else {
      rootData = extractBusinessUnitData({...hierarchyData, depth: 0});
    }

    // Create hierarchy
    const root = d3.hierarchy(rootData);

    // Function to toggle node - keep this for manual toggling
    const toggleNode = (d) => {
      const newCollapsedNodes = new Set(collapsedNodes);
      
      // Simple toggle
      if (newCollapsedNodes.has(d.data.id)) {
        newCollapsedNodes.delete(d.data.id);
      } else {
        newCollapsedNodes.add(d.data.id);
      }
      
      setCollapsedNodes(newCollapsedNodes);
    };

    const renderTree = () => {
      // Clear existing SVG content
      d3.select(svgRef.current).selectAll("*").remove();

      // Process the hierarchy to respect collapsed state
      const processNode = (node) => {
        // We want all employees to remain visible in the tree, even when deleted
        // So we're not skipping any employee nodes in the processNode function

        if (collapsedNodes.has(node.data.id)) {
          node._children = node.children;
          node.children = null;
        } else {
          node.children = node._children || node.children;
          node._children = null;
        }
        if (node.children) {
          node.children.forEach(processNode);
        }
      };

      // Clone the root to avoid mutating the original
      const processedRoot = root.copy();
      processNode(processedRoot);

      // Get visible nodes and links
      const nodes = processedRoot.descendants();
      const links = processedRoot.links();

    // Get the bounds of the tree
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    processedRoot.each(d => {
      minX = Math.min(minX, d.x);
      maxX = Math.max(maxX, d.x);
      minY = Math.min(minY, d.y);
      maxY = Math.max(maxY, d.y);
    });
    // Calculate height
    const height = nodes.length * barHeight + margin.top + margin.bottom;

    // Calculate dimensions based on the tree bounds
    const width = maxX - minX + margin.left + margin.right;

    // Create SVG with calculated dimensions
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .style("font", "12px sans-serif");

      // Create main group
      const g = svg.append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Calculate positions
      let i = 0;
      processedRoot.eachBefore(d => {
        d.x = d.depth * treeIndent;
        d.y = i * barHeight;
        i++;
      });

      // Add links
      g.selectAll(".link")
        .data(links)
        .enter()
        .append("path")
        .attr("class", "link")
        .attr("fill", "none")
        .attr("stroke", "#E2E8F0")
        .attr("stroke-width", 1)
        .attr("d", d => {
          return `M${d.source.x},${d.source.y + barHeight / 2}
                  L${d.source.x},${d.target.y + barHeight / 2}
                  L${d.target.x},${d.target.y + barHeight / 2}`;
        });

      // Add nodes
      const node = g.selectAll(".node")
        .data(nodes)
        .enter()
        .append("g")
        .attr("class", "node")
        .attr("transform", d => `translate(0,${d.y})`);

      // Add expand/collapse button
      node.append("g")
        .attr("class", "button-group")
        .attr("transform", d => `translate(${d.x - 15},${barHeight / 2})`)
        .style("cursor", "pointer")
        .style("display", d => d.data.hasChildren ? null : "none")
        .on("click", (event, d) => {
          event.stopPropagation();
          toggleNode(d);
        })
        .each(function(d) {
          const group = d3.select(this);
          group.append("circle")
            .attr("r", 7)
            .attr("fill", "white")
            .attr("stroke", "#94A3B8");
          
          group.append("text")
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("font-size", "10px")
            .attr("fill", "#64748B")
            .text(d => collapsedNodes.has(d.data.id) ? "+" : "−");
        });

      // Add node content
      const nodeGroup = node.append("g")
        .attr("class", "node-group")
        .attr("transform", d => `translate(${d.x},0)`);

      // Add node content
      nodeGroup.each(function(d) {
        const g = d3.select(this);

        if (d.data.type === 'employee') {
          // For employee nodes, start with the avatar
          const clipId = `clip-employee-${d.data.id}-${Math.random().toString(36).substr(2, 5)}`;
          
          // Get the assignment for this employee
          const assignment = employeeAssignments.get(d.data.id);
          
          // Check if employee is deleted (regardless of being a business unit owner)
          const isDeleted = assignment ? assignment.deleted === true : false;
          
          // An employee is considered attached if they have an assignment, regardless of deleted status
          // This allows us to show the deleted status visually but still maintain the node in the tree
          const isAttached = assignment !== undefined;
          
          // For visual styling - only show as attached if not deleted
          const showAsAttached = isAttached && !isDeleted;
          
          // Check if the employee belongs to a selectable project
          const canInteractWithEmployee = isEmployeeInSelectableProject(d.data);

          // Create a clipPath that is guaranteed to work
          const clipPath = g.append("defs")
            .append("clipPath")
            .attr("id", clipId);
            
          clipPath.append("circle")
            .attr("r", 12)
            .attr("cx", 28)
            .attr("cy", barHeight / 2);

          // First add a simple avatar circle with initial - this ensures something displays immediately
          const avatarCircle = g.append("circle")
            .attr("class", "avatar-placeholder")
            .attr("cx", 28)
            .attr("cy", barHeight / 2)
            .attr("r", 12)
            .attr("fill", "#E2E8F0")
            .attr("stroke", "#94A3B8")
            .attr("stroke-width", 1);

          const avatarText = g.append("text")
            .attr("class", "avatar-initial")
            .attr("x", 28)
            .attr("y", barHeight / 2)
            .attr("dy", "0.31em")
            .attr("text-anchor", "middle")
            .attr("fill", "#475569")
            .attr("font-size", "12px")
            .text(getInitials(d.data.fullName));
          
          // Load employee photo asynchronously without blocking
          if (d.data.azureId) {
            employeesApi.getEmployeePhoto(d.data.azureId)
              .then(photoUrl => {
                if (photoUrl) {
                  
                  // Remove the placeholder elements using class selectors
                  g.select(".avatar-placeholder").remove();
                  g.select(".avatar-initial").remove();
                  
                  // Create employee photo
                  const image = g.append("image")
                    .attr("x", 16)
                    .attr("y", barHeight / 2 - 12)
                    .attr("width", 24)
                    .attr("height", 24)
                    .attr("clip-path", `url(#${clipId})`)
                    .attr("preserveAspectRatio", "xMidYMid slice");
                  
                  // Set the href attribute directly
                  image.attr("href", photoUrl);
                  
                  // For SVG compatibility, also set the xlink:href
                  try {
                    image.node().setAttributeNS('http://www.w3.org/1999/xlink', 'href', photoUrl);
                  } catch (e) {
                    console.error('Error setting xlink:href:', e);
                  }
                  
                }
              })
              .catch(error => {
                console.error('Error loading employee photo:', error);
                // Placeholder already rendered, no need to do anything
              });
          }

          // Add name after avatar
          const nameText = g.append("text")
            .attr("x", 48)
            .attr("y", barHeight / 2 - 8)
            .attr("dy", "0.31em")
            .attr("fill", isDeleted ? "#94A3B8" : (showAsAttached ? "#2563EB" : "#475569"))
            .attr("font-weight", "500")
            .attr("font-size", "12px")
            .attr("text-decoration", isDeleted ? "line-through" : "none")
            .text(d.data.fullName);

          let currentX = 48 + nameText.node().getComputedTextLength() + 12;

          // Add position if available
          if (d.data.position) {
            g.append("text")
              .attr("x", currentX)
              .attr("y", barHeight / 2 - 8)
              .attr("dy", "0.31em")
              .attr("fill", isDeleted ? "#CBD5E1" : (showAsAttached ? "#3B82F6" : "#94A3B8"))
              .attr("font-size", "12px")
              .text(`• ${d.data.position.label}`);

            currentX += g.select("text:last-of-type").node().getComputedTextLength() + 12;
          }

          // Add grade if available and position requires it
          if (d.data.grade && d.data.position?.requiresGrade) {
            g.append("text")
              .attr("x", currentX)
              .attr("y", barHeight / 2 - 8)
              .attr("dy", "0.31em")
              .attr("fill", isDeleted ? "#CBD5E1" : (showAsAttached ? "#3B82F6" : "#94A3B8"))
              .attr("font-size", "12px")
              .text(`• ${d.data.grade.label}`);

            currentX += g.select("text:last-of-type").node().getComputedTextLength() + 12;
          }

          // Add roles if available (move this after template info)
          if (d.data.roles && d.data.roles.length > 0) {
            const rolesText = d.data.roles.map(role => role.label).join(', ');
            
            g.append("text")
              .attr("x", 48)
              .attr("y", barHeight / 2 + 8)
              .attr("dy", "0.31em")
              .attr("fill", isDeleted ? "#CBD5E1" : (showAsAttached ? "#3B82F6" : "#94A3B8"))
              .attr("font-size", "11px")
              .attr("font-style", "italic")
              .text(rolesText);
          }

          // Add business unit owner badge if applicable
          if (d.data.isBusinessUnitOwner) {
            const badgeGroup = g.append("g")
              .attr("transform", `translate(${currentX + 8}, ${barHeight / 2 - 8})`);
            
            badgeGroup.append("rect")
              .attr("x", 0)
              .attr("y", -8)
              .attr("width", 60)
              .attr("height", 16)
              .attr("rx", 8)
              .attr("fill", isDeleted ? "#F1F5F9" : "#ECFDF5")
              .attr("stroke", isDeleted ? "#CBD5E1" : "#A7F3D0")
              .attr("stroke-width", 1);
            
            badgeGroup.append("text")
              .attr("x", 30)
              .attr("y", 0)
              .attr("text-anchor", "middle")
              .attr("dy", "0.31em")
              .attr("fill", isDeleted ? "#94A3B8" : "#047857")
              .attr("font-size", "10px")
              .attr("font-weight", "600")
              .text("OWNER");
            
            currentX += 76; // Update currentX to account for the badge
          }
          
          // Add deleted badge if applicable
          if (isDeleted) {
            const badgeGroup = g.append("g")
              .attr("transform", `translate(${currentX + 8}, ${barHeight / 2 - 8})`);
            
            badgeGroup.append("rect")
              .attr("x", 0)
              .attr("y", -8)
              .attr("width", 70)
              .attr("height", 16)
              .attr("rx", 8)
              .attr("fill", "#FEF2F2")
              .attr("stroke", "#FECACA")
              .attr("stroke-width", 1);
            
            badgeGroup.append("text")
              .attr("x", 35)
              .attr("y", 0)
              .attr("text-anchor", "middle")
              .attr("dy", "0.31em")
              .attr("fill", "#DC2626")
              .attr("font-size", "10px")
              .attr("font-weight", "600")
              .text("REMOVED");
            
            currentX += 86; // Update currentX to account for the badge
          }

          // Add extra evaluee badge if applicable
          if (d.data.isExtraEvaluee) {
            const badgeGroup = g.append("g")
              .attr("transform", `translate(${currentX + 8}, ${barHeight / 2 - 8})`);
            
            badgeGroup.append("rect")
              .attr("x", 0)
              .attr("y", -8)
              .attr("width", 60)
              .attr("height", 16)
              .attr("rx", 8)
              .attr("fill", isDeleted ? "#F1F5F9" : "#EFF6FF")
              .attr("stroke", isDeleted ? "#CBD5E1" : "#BFDBFE")
              .attr("stroke-width", 1);
            
            badgeGroup.append("text")
              .attr("x", 30)
              .attr("y", 0)
              .attr("text-anchor", "middle")
              .attr("dy", "0.31em")
              .attr("fill", isDeleted ? "#94A3B8" : "#2563EB")
              .attr("font-size", "10px")
              .attr("font-weight", "600")
              .text("EXTRA");
            
            currentX += 76; // Update currentX to account for the badge
          }

          // Add template info if available
          const template = getEmployeeTemplate(d.data.id);
          if (template && !isDeleted) {
            // First measure the text width
            const tempTextGroup = g.append("text")
              .attr("x", 0)
              .attr("y", 0)
              .attr("opacity", 0) // Make it invisible
              .text(`Skills Matrix: ${template.skillsMatrix.name}`);
            
            const matrixTextWidth = tempTextGroup.node().getComputedTextLength();
            
            // Remove the temporary element
            tempTextGroup.remove();
            
            // Now add the actual visible text
            g.append("text")
              .attr("x", 48)
              .attr("y", barHeight / 2 + (d.data.roles && d.data.roles.length > 0 ? 24 : 8))
              .attr("dy", "0.31em")
              .attr("fill", isDeleted ? "#CBD5E1" : (showAsAttached ? "#2563EB" : "#475569"))
              .attr("font-size", "11px")
              .attr("font-style", "italic")
              .text(`Skills Matrix: ${template.skillsMatrix.name}`);
              
            // Update currentX to ensure we account for matrix label width if it's longer than other elements
            currentX = Math.max(currentX, 48 + matrixTextWidth + 12);
          }

          // Add action buttons
          // Add attach/detach button
          const buttonGroup = g.append("g")
            .attr("transform", `translate(${currentX + 48}, ${barHeight / 2})`) // Increase spacing from 32 to 48
            .style("cursor", !canInteractWithEmployee || readOnly ? "default" : "pointer")
            .style("display", !canInteractWithEmployee || readOnly ? "none" : null)
            //.on("click", (event) => {
              //event.stopPropagation();
              //if (readOnly || !canInteractWithEmployee) return;
              //handleEmployeeToggle(d.data);
            //});

          buttonGroup.append("title")
            .text(!isDeleted ? "Detach from session" : "Attach to session");

          // Show - for attached employees, + for deleted or unattached employees
          const isAttachedAndNotDeleted = isAttached && !isDeleted;
          
          // Use blue for unattached/deleted employees (showing +)
          // Use red for attached employees (showing -)
          buttonGroup.append("circle")
            .attr("r", 8)
            .attr("fill", isAttachedAndNotDeleted ? "#DC2626" : "#2563EB");

          buttonGroup.append("path")
            .attr("d", isAttachedAndNotDeleted ? "M-4 0 H4" : "M-4 0 H4 M0 -4 V4")
            .attr("stroke", "white")
            .attr("stroke-width", 2);

          // Calculate the final total width needed for the background
          // Include the button position plus its radius and extra padding
          const totalWidth = Math.max(currentX + 80, 350); // Ensure minimum width of 350px

          // Add background highlight for attached employees after all elements are created
          // Insert the background rect as the first child of the group
          g.insert("rect", ":first-child")
            .attr("x", 4)
            .attr("y", -barHeight/8 + 18)
            .attr("width", totalWidth)
            .attr("height", barHeight - 8)
            .attr("rx", 4)
            .attr("fill", isDeleted ? "#F9FAFB" : (showAsAttached ? "#EFF6FF" : "transparent"))
            .attr("stroke", isDeleted ? "#E5E7EB" : (showAsAttached ? "#BFDBFE" : "#E2E8F0"))
            .attr("stroke-width", 1)
            .attr("stroke-dasharray", isDeleted ? "4 2" : "none")
            .attr("opacity", !canInteractWithEmployee ? 0.4 : (isDeleted ? 0.7 : (showAsAttached ? 0.9 : 1)))
            .style("pointer-events", "none");

          // Add a separate invisible clickable layer on top of all content
          g.append("rect")
              .attr("x", 4)
              .attr("y", -barHeight/8 + 18)
              .attr("width", totalWidth)
              .attr("height", barHeight - 8)
              .attr("rx", 4)
              .attr("fill", "transparent")
              .style("cursor", !canInteractWithEmployee || readOnly ? "default" : "pointer")
              .on("click", (event) => {
                event.stopPropagation();
                if (readOnly || !canInteractWithEmployee) return;
                handleEmployeeClick(d.data);
              });

          const canShowOverrideButton = isAttached && !isDeleted && !readOnly && canInteractWithEmployee;
          if (canShowOverrideButton) {
            const buttonX = 4 + totalWidth + 14;
            const buttonY = -barHeight / 8 + 26;

            const overrideButtonGroup = g.append("g")
              .attr("transform", `translate(${buttonX}, ${buttonY})`)
              .style("cursor", "pointer");

            overrideButtonGroup.append("title")
              .text("Change skills matrix");

            overrideButtonGroup.append("circle")
              .attr("r", 8)
              .attr("fill", "#6366F1");

            overrideButtonGroup.append("path")
              .attr("d", "M-2.5 2.5 L-3.5 4.5 L-1.5 4.8 L3.5 -0.2 L1.8 -1.9 L-2.5 2.5 Z")
              .attr("fill", "white");

            overrideButtonGroup.on("click", (event) => {
              event.stopPropagation();
              handleOpenMatrixOverride(d.data);
            });
          }

        } else {
          // For non-employee nodes (team, project, businessUnit)
          const nameText = g.append("text")
            .attr("x", 8)
            .attr("y", barHeight / 2)
            .attr("dy", "0.31em")
            .attr("fill", "#0F172A")
            .attr("font-weight", "500")
            .attr("font-size", "12px")
            .text(d.data.name);

          // For project nodes, add select/unselect button (but not in individual mode)
          if (d.data.type === 'project' && !isIndividualMode) {
            const nameWidth = nameText.node().getComputedTextLength();
            const badgeX = 8 + nameWidth + 16;
            const buttonX = badgeX + 90; // Increased spacing after badge before button
            
            // Calculate total width for the background (add more space for the button and badge)
            const totalWidth = buttonX + 30; // Increased to accommodate button after badge
            
            // Check if project is selected
            const isProjectSelected = (selectedProjects || new Set()).has(d.data.id);
            
            // Determine if the user can interact with projects
            const canInteractWithProject = canSelectProjects() && !readOnly;

            // Add background for all project rows (selected and unselected)
            g.insert("rect", ":first-child")
              .attr("x", 4)
              .attr("y", -barHeight/8 + 18)
              .attr("width", totalWidth)
              .attr("height", barHeight - 8)
              .attr("rx", 4)
              .attr("fill", isProjectSelected ? "#F0FDF4" : "#F8FAFC") // Light gray background for unselected projects
              .attr("stroke", isProjectSelected ? "#A7F3D0" : "#E2E8F0")
              .attr("stroke-width", 1)
              .attr("opacity", 0.9)
              .style("pointer-events", "none");

            // Add a "PROJECT" label badge first (before the selection button)
            const badgeGroup = g.append("g")
              .attr("transform", `translate(${badgeX}, ${barHeight / 2 - 8})`);
            
            badgeGroup.append("rect")
              .attr("x", 0)
              .attr("y", -8)
              .attr("width", 60)
              .attr("height", 16)
              .attr("rx", 8)
              .attr("fill", isProjectSelected ? "#ECFDF5" : "#F1F5F9")
              .attr("stroke", isProjectSelected ? "#A7F3D0" : "#CBD5E1")
              .attr("stroke-width", 1);
            
            badgeGroup.append("text")
              .attr("x", 30)
              .attr("y", 0)
              .attr("text-anchor", "middle")
              .attr("dy", "0.31em")
              .attr("fill", isProjectSelected ? "#065F46" : "#64748B")
              .attr("font-size", "10px")
              .attr("font-weight", "600")
              .text("PROJECT");

            // Add selection indicator for projects
            const projectButtonGroup = g.append("g")
              .attr("transform", `translate(${buttonX}, ${barHeight / 2})`)
              .style("cursor", canInteractWithProject ? "pointer" : "default")
              .style("display", canInteractWithProject ? null : "none")
              .on("click", (event) => {
                event.stopPropagation();
                if (!canInteractWithProject) return;
                handleProjectToggle(d.data);
              });

            // Create tooltip only if the user can interact with projects
            if (canInteractWithProject) {
              projectButtonGroup.append("title")
                .text(isProjectSelected ? "Unselect Project" : "Select Project");
            }

            // Create rectangular button instead of circle for projects
            projectButtonGroup.append("rect")
              .attr("x", -8)
              .attr("y", -8)
              .attr("width", 16)
              .attr("height", 16)
              .attr("rx", 3)  // Rounded corners
              .attr("fill", "#059669");  // Green colors for projects

            // Add appropriate icon based on selection state
            if (isProjectSelected) {
              // Use checkmark icon for selected state
              projectButtonGroup.append("path")
                .attr("d", "M-5 0 L-2 3 L5 -4")  // Checkmark shape
                .attr("fill", "none")
                .attr("stroke", "white")
                .attr("stroke-width", 2)
                .attr("stroke-linecap", "round")
                .attr("stroke-linejoin", "round");
            } else {
              // Use empty square for unselected state
              projectButtonGroup.append("rect")
                .attr("x", -6)
                .attr("y", -6)
                .attr("width", 12)
                .attr("height", 12)
                .attr("fill", "white")
                .attr("stroke", "white")
                .attr("stroke-width", 1);
            }
              
            // Add a clickable overlay for the entire project row using the same totalWidth
            // Only add if the user can interact with projects
            if (canInteractWithProject) {
              g.append("rect")
                .attr("x", 4)
                .attr("y", -barHeight/8 + 18)
                .attr("width", totalWidth) // Use same totalWidth from above
                .attr("height", barHeight - 8)
                .attr("rx", 4)
                .attr("fill", "transparent")
                .style("cursor", "pointer")
                .on("click", (event) => {
                  event.stopPropagation();
                  if (!canInteractWithProject) return;
                  handleProjectToggle(d.data);
                });
            }
          }

          // Add manager information if exists
          if (d.data.manager) {
            // Create a unique clip path ID
            const managerClipId = `clip-manager-${d.data.id}-${Math.random().toString(36).substr(2, 5)}`;
            
            // Position calculations
            const nameWidth = nameText.node().getComputedTextLength();
            const startX = 8 + nameWidth + 12;
            
            // Create clip path
            const clipDefs = g.append("defs")
              .append("clipPath")
              .attr("id", managerClipId);
              
            clipDefs.append("circle")
              .attr("r", 8)
              .attr("cx", startX + 8)
              .attr("cy", barHeight / 2);

            // First add a simple avatar circle with initial for manager
            g.append("circle")
              .attr("class", "manager-avatar-placeholder")
              .attr("cx", startX + 8)
              .attr("cy", barHeight / 2)
              .attr("r", 8)
              .attr("fill", "#E2E8F0")
              .attr("stroke", "#94A3B8")
              .attr("stroke-width", 1);

            g.append("text")
              .attr("class", "manager-avatar-initial")
              .attr("x", startX + 8)
              .attr("y", barHeight / 2)
              .attr("dy", "0.31em")
              .attr("text-anchor", "middle")
              .attr("fill", "#475569")
              .attr("font-size", "10px")
              .text(getInitials(d.data.manager.fullName));
            
            // Load manager photo asynchronously without blocking
            if (d.data.manager.azureId) {
              employeesApi.getEmployeePhoto(d.data.manager.azureId)
                .then(photoUrl => {
                  if (photoUrl) {
                    // Remove the placeholder elements using class selectors
                    g.select(".manager-avatar-placeholder").remove();
                    g.select(".manager-avatar-initial").remove();
                    
                    // Create manager photo
                    const image = g.append("image")
                      .attr("x", startX)
                      .attr("y", barHeight / 2 - 8)
                      .attr("width", 16)
                      .attr("height", 16)
                      .attr("clip-path", `url(#${managerClipId})`)
                      .attr("preserveAspectRatio", "xMidYMid slice");
                    
                    // Set the href attribute directly
                    image.attr("href", photoUrl);
                    
                    // For SVG compatibility, also set the xlink:href
                    try {
                      image.node().setAttributeNS('http://www.w3.org/1999/xlink', 'href', photoUrl);
                    } catch (e) {
                      console.error('Error setting xlink:href:', e);
                    }
                    
                  }
                })
                .catch(error => {
                  console.error('Error loading manager photo:', error);
                });
            }

            // Add text after the avatar
            g.append("text")
              .attr("x", startX + 24)
              .attr("y", barHeight / 2)
              .attr("dy", "0.31em")
              .attr("fill", "#475569")
              .attr("font-size", "12px")
              .text(d.data.manager.fullName);
          }
        }
      });
    };

    renderTree();

  }, [hierarchyData, collapsedNodes, employeeAssignments, readOnly, extraEvaluees, canViewBusinessUnitOwner, selectedProjects, isIndividualMode]);

  // Render matrix selection dialog
  const renderMatrixDialog = () => (
    <Dialog
      open={isMatrixDialogOpen}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus={false}
      keepMounted={false}
      disableAutoFocus={false}
      disablePortal={false}
      aria-labelledby="matrix-dialog-title"
    >
      <DialogTitle id="matrix-dialog-title">
        Select Skills Matrix for {selectedEmployee?.fullName || selectedExtraEvaluee?.fullName}
      </DialogTitle>
      <DialogContent>
        <SkillsMatrixSelectionList
          matrices={uniqueMatrices}
          getMatrixRoles={getMatrixRoles}
          onSelect={(matrix) => selectedExtraEvaluee
            ? handleExtraEvalueeMatrixSelect(matrix, selectedExtraEvaluee)
            : handleMatrixSelect(matrix)
          }
          emptyDescription="There are no skills matrices configured for this session template that match this individual's attributes."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  // Render extra evaluees dialog
  const renderExtraEvalueesDialog = () => (
    <Dialog
      open={isExtraEvalueesDialogOpen}
      onClose={handleCloseExtraEvalueesDialog}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus={false}
      keepMounted={false}
      disableAutoFocus={false}
      disablePortal={false}
      aria-labelledby="extra-evaluees-dialog-title"
    >
      <DialogTitle id="extra-evaluees-dialog-title">
        Add Extra Evaluees
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <EmployeeSearch
            value={selectedExtraEvaluee}
            onChange={handleExtraEvalueeSelect}
            label="Select Individual"
            placeholder="Search individuals..."
            width={400}
            disabledEmployeeIds={getDisabledEmployeeIds()}
            showSelectedAvatar={true}
            sx={{ width: '100%' }}
          />
        </Box>

        {selectedExtraEvaluee && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" component="div" sx={{ mb: 1, color: '#475569' }}>
              Selected employee:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              p: 1.5, 
              borderRadius: 1, 
              border: '1px solid #E2E8F0',
              backgroundColor: '#F8FAFC'
            }}>
              <EmployeeAvatar employee={selectedExtraEvaluee} size={32} sx={{ mr: 1.5 }} />
              <Box>
                <Typography variant="body1" component="div" sx={{ fontWeight: 500 }}>
                  {selectedExtraEvaluee.fullName}
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: '#64748B' }}>
                  {selectedExtraEvaluee.position?.label}
                  {selectedExtraEvaluee.grade && ` • ${selectedExtraEvaluee.grade.label}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseExtraEvalueesDialog}>Cancel</Button>
        {selectedExtraEvaluee && (
          <Button 
            onClick={() => {
              setIsMatrixDialogOpen(true);
              setIsExtraEvalueesDialogOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            Select Matrix
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  // Extract all employee IDs from the hierarchy data
  const getAllEmployeeIdsFromHierarchy = (hierarchyData) => {
    if (!hierarchyData) return new Set();
    
    const employeeIds = new Set();
    
    // Add the business unit owner if exists
    if (hierarchyData.owner) {
      employeeIds.add(hierarchyData.owner.id);
    }
    
    // Process child business units
    if (hierarchyData.childBus) {
      hierarchyData.childBus.forEach(childBu => {
        // Add business unit owner if exists
        if (childBu.owner) {
          employeeIds.add(childBu.owner.id);
        }
      });
    }
    
    // Process projects
    if (hierarchyData.projects) {
      hierarchyData.projects.forEach(project => {
        // Process teams in the project
        if (project.teams) {
          project.teams.forEach(team => {
            // Process team recursively
            collectEmployeeIdsFromTeam(team, employeeIds);
          });
        }
      });
    }
    
    return employeeIds;
  };
  
  // Helper function to collect employee IDs from a team and its children
  const collectEmployeeIdsFromTeam = (team, employeeIds) => {
    // Add team manager if exists
    /*if (team.manager) {
      employeeIds.add(team.manager.id);
    }*/
    
    // Add team employees
    if (team.employees) {
      team.employees.forEach(employee => {
        employeeIds.add(employee.id);
      });
    }
    
    // Process child teams
    if (team.childTeams) {
      team.childTeams.forEach(childTeam => {
        collectEmployeeIdsFromTeam(childTeam, employeeIds);
      });
    }
  };

  // Helper function to restore a business unit owner
  const handleRestoreBusinessUnitOwner = (employee) => {
    const assignment = employeeAssignments.get(employee.id);
    if (!assignment) return;
    
    // Find the matrix that was previously used
    if (assignment.matrix) {
      // Re-select the employee with the same matrix
      onEmployeeSelection(employee.id, employee, assignment.matrix);
      
      // Restore reviewer assignments if they existed
      if (assignment.reviewers && assignment.reviewers.length > 0 && onReviewerAssignment) {
        onReviewerAssignment(employee.id, assignment.reviewers);
      } else {
        // Set up default reviewer assignments
        setupDefaultReviewers(employee, assignment.matrix);
      }
    } else {
      // If no matrix was saved, show the matrix selection dialog
      setSelectedEmployee(employee);
      setIsMatrixDialogOpen(true);
    }
  };

  // Add helper function to find a project by ID in the hierarchy
  const findProjectById = (businessUnitData, projectId) => {
    if (!businessUnitData || !businessUnitData.projects) return null;
    
    // Look for the project in the top-level projects
    const project = businessUnitData.projects.find(p => p.id === projectId);
    if (project) return project;
    
    // If not found, search in child business units recursively
    if (businessUnitData.childBus) {
      for (const childBu of businessUnitData.childBus) {
        const foundProject = findProjectById(childBu, projectId);
        if (foundProject) return foundProject;
      }
    }
    
    return null;
  };

  // Update the handler to use the prop-based onProjectSelection
  const handleProjectToggle = (project) => {
    if (!onProjectSelection) return; // Guard clause if no handler is provided
    
    // Create a new Set from the current selectedProjects
    const newSelectedProjects = new Set(selectedProjects || new Set());
    
    // Toggle the project selection
    const isCurrentlySelected = newSelectedProjects.has(project.id);
    
    if (isCurrentlySelected) {
      // If project was selected, unselect it
      newSelectedProjects.delete(project.id);
      
      // Mark all employees in this project as deleted
      markProjectEmployeesAsDeleted(project.id);
      
      // Don't show alert, let parent component handle feedback
      // showAlert(`Project "${project.name}" unselected`, 'info');
    } else {
      // If project was not selected, select it
      newSelectedProjects.add(project.id);
      // Don't show alert, let parent component handle feedback
      // showAlert(`Project "${project.name}" selected`, 'info');
    }
    
    // Call the parent's handler instead of updating local state
    onProjectSelection(newSelectedProjects);
  };
  
  // Helper function to mark all employees in a project as deleted
  const markProjectEmployeesAsDeleted = (projectId) => {
    if (!hierarchyData) return;
    
    // Find the project in the hierarchy
    const project = findProjectById(hierarchyData, projectId);
    if (!project) return;
    
    // Get all employee IDs from the project
    const employeeIds = getAllEmployeeIdsFromProject(project);
    
    // Mark each employee as deleted
    employeeIds.forEach(employeeId => {
      if (employeeAssignments.has(employeeId)) {
        onEmployeeSelection(employeeId, employeeAssignments.get(employeeId).employee, null);
      }
    });
  };
  
  // Helper function to get all employee IDs from a project
  const getAllEmployeeIdsFromProject = (project) => {
    const employeeIds = new Set();
    
    // Process teams in the project
    if (project.teams) {
      project.teams.forEach(team => {
        // Process team recursively
        collectEmployeeIdsFromTeam(team, employeeIds);
      });
    }
    
    return employeeIds;
  };

  // Helper function to find and select the project that an employee belongs to
  const selectEmployeeProject = (employee) => {
    if (!hierarchyData || !onProjectSelection || employee.isBusinessUnitOwner || employee.isExtraEvaluee) {
      // Skip for business unit owners and extra evaluees as they're not in projects
      return;
    }
    
    // Find the project ID for this employee
    const projectId = findProjectIdForEmployee(hierarchyData, employee.id);
    
    if (projectId) {
      // Create a new Set from the current selectedProjects
      const newSelectedProjects = new Set(selectedProjects || new Set());
      
      // Add the project to selected projects if not already selected
      if (!newSelectedProjects.has(projectId)) {
        newSelectedProjects.add(projectId);
        // Call the parent's handler
        onProjectSelection(newSelectedProjects);
      }
    }
  };
  
  // Helper function to find the project ID that an employee belongs to
  const findProjectIdForEmployee = (businessUnit, employeeId) => {
    if (!businessUnit || !businessUnit.projects) return null;
    
    // Check each project
    for (const project of businessUnit.projects) {
      // Search teams in this project for the employee
      if (project.teams) {
        for (const team of project.teams) {
          // Check if employee is in this team
          const found = searchTeamForEmployee(team, employeeId);
          if (found) {
            return project.id;
          }
        }
      }
    }
    
    // Check child business units recursively
    if (businessUnit.childBus) {
      for (const childBu of businessUnit.childBus) {
        const foundProjectId = findProjectIdForEmployee(childBu, employeeId);
        if (foundProjectId) {
          return foundProjectId;
        }
      }
    }
    
    return null;
  };
  
  // Helper function to search a team and its children for an employee
  const searchTeamForEmployee = (team, employeeId) => {
    // Check if employee is in this team
    if (team.employees && team.employees.some(emp => emp.id === employeeId)) {
      return true;
    }
    
    // Check child teams recursively
    if (team.childTeams) {
      for (const childTeam of team.childTeams) {
        if (searchTeamForEmployee(childTeam, employeeId)) {
          return true;
        }
      }
    }
    
    return false;
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading team structure..." />;
  }
  
  // In individual mode without hierarchy data, show only the extra evaluees UI
  if (isIndividualMode && !hierarchyData) {
    return (
      <Box>
              {!readOnly && canCreateExtraEvaluees() && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <HeaderButton
            onClick={handleOpenExtraEvalueesDialog}
            sx={{
              minWidth: 'auto',
              height: '40px',
              padding: '0 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              '&:hover': {
                backgroundColor: '#1d4ed8',
              }
            }}
          >
            <PersonAddIcon style={{ marginRight: '8px', fontSize: '20px' }} /> Attach Extra Evaluees
          </HeaderButton>
        </Box>
      )}
        
        {/* Show selected employees */}
        {extraEvaluees.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Selected Employees</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {extraEvaluees.map((evaluee) => {
                const assignment = employeeAssignments.get(evaluee.id);
                if (assignment?.deleted) return null;
                
                return (
                  <Chip
                    key={evaluee.id}
                    label={evaluee.fullName}
                    onDelete={!readOnly ? () => {
                      setExtraEvaluees(prev => prev.filter(e => e.id !== evaluee.id));
                      onEmployeeSelection(evaluee.id, null, null);
                    } : undefined}
                    sx={{
                      backgroundColor: '#F3F4F6',
                      '& .MuiChip-deleteIcon': {
                        color: '#6B7280',
                        '&:hover': {
                          color: '#EF4444',
                        }
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}
        
        {renderMatrixDialog()}
        {renderExtraEvalueesDialog()}
      </Box>
    );
  }


  return (
    <Box>
      {!readOnly && canCreateExtraEvaluees() && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <HeaderButton
            onClick={handleOpenExtraEvalueesDialog}
            sx={{
              minWidth: 'auto',
              height: '32px',
              padding: '0 16px',
              backgroundColor: '#2563eb',
              color: 'white',
              alignSelf: 'flex-start',
              '&:hover': {
                backgroundColor: '#1d4ed8',
              }
            }}
          >
            <PersonAddIcon style={{ marginRight: '8px', fontSize: '18px' }} /> Attach Extra Evaluees
          </HeaderButton>
        </Box>
      )}
      <Box 
        sx={{ 
          border: '1px solid #E2E8F0',
          borderRadius: 2,
          p: 2,
          backgroundColor: '#FFFFFF',
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '600px',
        }}
      >
        <svg 
          ref={svgRef}
          style={{
            width: '100%',
            minWidth: '800px',
            display: 'block',
            margin: 0,
          }}
        />
      </Box>
      {renderMatrixDialog()}
      {renderExtraEvalueesDialog()}
    </Box>
  );
};

export default TeamStructureView; 
